<template>
    <v-bottom-navigation
        v-if="isAuthenticated"
        class="app-bottom-bar"
    >
        <div class="app-bottom-bar__items">
            <v-btn to="/home" @click="closePanels">
                <span class="white--text">Home</span>
                <v-icon small color="#ffffff">mdi-home</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn @click="handleMapClick">
                <span class="white--text">Geofence</span>
                <v-icon small color="#ffffff">mdi-map-marker-radius</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn class="settings-nav-button"
                   @pointerdown="startSettingsHold"
                   @pointerup="cancelSettingsHold"
                   @pointercancel="cancelSettingsHold"
                   @pointerleave="cancelSettingsHold"
                   @contextmenu.prevent
                   @click="handleSettingsClick">
                <span class="white--text">Settings</span>
                <v-icon small color="#ffffff">mdi-cog</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn to="/event-log" @click="closePanels">
                <span class="white--text">Event log</span>
                <v-icon small :color="alarmColor" @click="dismissAlarmMode">mdi-alert-circle</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn to="/weather" @click="closePanels">
                <span class="white--text">Weather</span>
                <v-icon small color="#ffffff">mdi-white-balance-sunny</v-icon>
            </v-btn>
        </div>
    </v-bottom-navigation>
</template>
<script lang="ts">import { defineComponent, computed, getCurrentInstance, onBeforeUnmount, ref } from "vue";
    import Vue from "vue";
    import { alarm, app } from "@/store";
    import { OidcAuth } from "vue-oidc-client/vue2";
    import {
        resolveGeolocationAccess,
    } from "@/utilities/useGeolocation";

    export default defineComponent({
        setup() {
            const root = getCurrentInstance()?.proxy as any;
            const settingsHoldTimer = ref<ReturnType<typeof setTimeout> | null>(null);
            const settingsLongPressTriggered = ref(false);

            onBeforeUnmount(() => {
                cancelSettingsHold();
            });

            const isAuthenticated = computed(() =>
                Vue.prototype.$oidc
                    ? (Vue.prototype.$oidc as OidcAuth).isAuthenticated
                    : false
            );

            const goToMap = async () => {
                if (root.$route.path !== "/map" && root.$route.path !== "/geo-permission") {
                    const accessResolution = await resolveGeolocationAccess();
                    console.info("[GeoTrace] FooterNavigation.goToMap resolved permission state", {
                        permissionState: accessResolution.permissionState,
                        accessStatus: accessResolution.accessStatus,
                        route: root.$route.path,
                    });

                    if (accessResolution.accessStatus === "denied" || accessResolution.accessStatus === "unavailable") {
                        root.$router.push({ name: "geo-permission", query: { destination: "map" } });
                        return;
                    }

                    if (accessResolution.accessStatus === "granted") {
                        root.$router.push({ name: "map" });
                    } else {
                        root.$router.push({ name: "geo-permission", query: { destination: "map" } });
                    }
                }
            };

            const closePanels = () => {
                app.setHomePanelOpen(false);
                app.setHomePanelIndex(-1);
                app.setMyBoatPanelOpen(false);
                app.setMyBoatPanelIndex(-1);
                app.setControlPanelOpen(false);
                app.setControlPanelIndex(-1);
                app.setSensorsPanelOpen(false);
                app.setSensorsPanelIndex(-1);
            };

            const cancelSettingsHold = () => {
                if (settingsHoldTimer.value !== null) {
                    clearTimeout(settingsHoldTimer.value);
                    settingsHoldTimer.value = null;
                }
            };

            const startSettingsHold = () => {
                cancelSettingsHold();
                settingsLongPressTriggered.value = false;
                settingsHoldTimer.value = setTimeout(() => {
                    settingsHoldTimer.value = null;
                    settingsLongPressTriggered.value = true;
                    closePanels();
                    root.$router.push({ name: "ui-showcase" });
                }, 5000);
            };

            const handleSettingsClick = () => {
                cancelSettingsHold();
                if (settingsLongPressTriggered.value) {
                    settingsLongPressTriggered.value = false;
                    return;
                }
                closePanels();
                root.$router.push({ name: "settings" });
            };

            const handleMapClick = () => {
                closePanels();
                goToMap();
            };

            const dismissAlarmMode = () => {
                alarm.setInAlarmMode(false);
            };

            const isAlarm = computed<boolean>(() => {
                return alarm.inAlarmMode;
            });

            const alarmColor = computed(() => {
                if (isAlarm.value) {
                    return "#FF0000"
                } else {
                    return "#FFFFFF"
                }
            });

            return {
                isAuthenticated,
                goToMap,
                closePanels,
                startSettingsHold,
                cancelSettingsHold,
                handleSettingsClick,
                handleMapClick,
                alarmColor,
                dismissAlarmMode
            };
        }
    });</script>

<style lang="scss" scoped>
    .app-bottom-bar {
        position: relative;
        flex: 0 0 auto;
        border-top-color: #fff;
        border-top-style: solid;
        border-top-width: 1px;
        background-color: #242021 !important;
        box-sizing: border-box;
        height: calc(56px + env(safe-area-inset-bottom, 0px)) !important;
        padding-bottom: var(--safe-area-inset-bottom, 0px);
        // ~7vw clears curved bezels (~30px on a 412px phone); device safe-area wins if larger.
        padding-left: max(7vw, env(safe-area-inset-left, 0px));
        padding-right: max(7vw, env(safe-area-inset-right, 0px));
        justify-content: center;

        // Buttons spread evenly across the available width, capped on wide screens.
        .app-bottom-bar__items {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr) 2px) minmax(0, 1fr);
            width: 100%;
            max-width: 36rem;
            height: 100%;

            .v-btn {
                width: 100%;
                min-width: 0;
                max-width: none;
                padding: 0 clamp(2px, 1vw, 16px);
                font-size: clamp(var(--app-font-tiny), 2.8vw, var(--app-font-small));
                white-space: nowrap;

                @media (max-width: 480px) {
                    letter-spacing: normal;
                }
            }
        }

        .spacer {
            flex-grow: 0 !important;
            background-color: var(--app-color-secondary);
            width: 2px;
            margin: 20px 0 20px 0;
        }
    }

    .settings-nav-button {
        -webkit-touch-callout: none;
        user-select: none;
    }

    @supports (-webkit-touch-callout: none) {
        @media (display-mode: standalone) {
            .app-bottom-bar {
                height: 56px !important;
                padding-bottom: 0 !important;
            }
        }
    }
</style>